import type {
  MaintenanceBannerFlag,
  MaintenanceFlagName,
  MaintenanceSeverity,
  ModuleName,
} from './MaintenanceBanner.types';

const MODULE_TO_FLAG: Partial<Record<ModuleName, MaintenanceFlagName>> = {
  Manage: 'manageMaintenanceBanner',
  Train: 'traineeMaintenanceBanner',
};

const VALID_SEVERITIES = new Set<MaintenanceSeverity>([
  'info',
  'warning',
  'danger',
]);

const SAFE_URL_PROTOCOLS = new Set(['https:']);

const DEFAULT_BANNER: MaintenanceBannerFlag = {
  enabled: false,
  message: '',
  severity: 'info',
  dismissible: false,
  action: null,
};

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return SAFE_URL_PROTOCOLS.has(parsed.protocol);
  } catch {
    return false;
  }
}

function isValidAction(
  value: unknown
): value is { label: string; url: string } {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.label === 'string' &&
    typeof obj.url === 'string' &&
    isSafeUrl(obj.url)
  );
}

function parseMaintenanceBannerFlag(flag: unknown): MaintenanceBannerFlag {
  if (typeof flag !== 'object' || flag === null) {
    return DEFAULT_BANNER;
  }

  const obj = flag as Record<string, unknown>;

  if (typeof obj.enabled !== 'boolean') return DEFAULT_BANNER;
  if (typeof obj.message !== 'string') return DEFAULT_BANNER;
  if (
    typeof obj.severity !== 'string' ||
    !VALID_SEVERITIES.has(obj.severity as MaintenanceSeverity)
  ) {
    return DEFAULT_BANNER;
  }
  if (typeof obj.dismissible !== 'boolean') return DEFAULT_BANNER;
  if (obj.action !== null && !isValidAction(obj.action)) return DEFAULT_BANNER;

  return {
    enabled: obj.enabled,
    message: obj.message,
    severity: obj.severity as MaintenanceSeverity,
    dismissible: obj.dismissible,
    action: obj.action as MaintenanceBannerFlag['action'],
  };
}

export { DEFAULT_BANNER, MODULE_TO_FLAG, parseMaintenanceBannerFlag };
